const isRecord = value => value && typeof value === 'object' && !Array.isArray(value)

const parseJsonRecord = value => {
  if (isRecord(value)) return value
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    const parsed = JSON.parse(value)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

const firstPresent = values => values.find(value => value !== undefined && value !== null)

const firstNonBlankString = values => values
  .map(value => String(value ?? '').trim())
  .find(Boolean)

const resolvePipelineType = values => {
  const pipelines = values
    .map(value => String(value ?? '').trim())
    .filter(Boolean)
  // Older task/setting endpoints commonly return the generic `3dgs` value
  // beside a newer, more precise pipeline identifier. Preserve the precise
  // value regardless of which response happened to contain it.
  return pipelines.find(value => value.toLowerCase() !== '3dgs') || pipelines[0]
}

/** Defaults and editable ranges from 3dgs_web/test RenderTask.vue. */
export const RENDER_TASK_DEFAULT_SETTINGS = Object.freeze({
  moveSpeed: 10,
  orbitSpeed: 24,
  autoRotateSpeed: 45,
  pinchSpeed: 0.8,
  originalFov: 75,
  backgroundColor: '#000000',
  orbitMotionType: 'orbit'
})

export const RENDER_TASK_SETTING_RANGES = Object.freeze({
  moveSpeed: Object.freeze([0.1, 40]),
  orbitSpeed: Object.freeze([1, 60]),
  autoRotateSpeed: Object.freeze([5, 180]),
  pinchSpeed: Object.freeze([0.1, 5]),
  originalFov: Object.freeze([30, 150])
})

const WEB_SPATIAL_MOVE_SPEED = 12
const WEB_SPATIAL_PINCH_SPEED = 1.2
const MINIAPP_SPATIAL_MOVE_SPEED = 7
const MINIAPP_SPATIAL_PINCH_SPEED = 1.2
const MINIAPP_OBJECT_MOVE_SPEED = 1
const IMAGE_TO_3DGS_MOVE_SPEED = 5

/** Match RenderTask.resolveTaskDetailPayload(response.data). */
export function resolveTaskDetailPayload(responsePayload) {
  const root = parseJsonRecord(responsePayload) || responsePayload
  const data = root?.data ?? root
  return data?.task ?? data?.task_detail ?? data ?? null
}

/**
 * Resolve the setting object while retaining model metadata that RenderTask
 * also reads from the response envelope.
 */
export function resolveModelSettingPayload(responsePayload) {
  const root = parseJsonRecord(responsePayload) || {}
  const rawResponseData = Object.prototype.hasOwnProperty.call(root, 'data') ? root.data : root
  const responseData = parseJsonRecord(rawResponseData) || rawResponseData
  const nested = [responseData?.data, responseData?.setting, responseData?.settings]
    .map(value => parseJsonRecord(value) || value)
    .find(isRecord) || responseData
  const setting = isRecord(nested) ? { ...nested } : {}

  const pipelineType = firstPresent([
    root.pipeline_type,
    root.pipelineType,
    responseData?.pipeline_type,
    responseData?.pipelineType,
    setting.pipeline_type,
    setting.pipelineType
  ])
  const modelFormat = firstPresent([
    root.model_format,
    root.modelFormat,
    responseData?.model_format,
    responseData?.modelFormat,
    setting.model_format,
    setting.modelFormat
  ])

  if (pipelineType !== undefined && setting.pipeline_type === undefined && setting.pipelineType === undefined) {
    setting.pipeline_type = pipelineType
  }
  if (modelFormat !== undefined && setting.model_format === undefined && setting.modelFormat === undefined) {
    setting.model_format = modelFormat
  }
  return setting
}

export function mergeTaskModelMetadata(task, setting = {}, hints = {}) {
  const source = isRecord(task) ? task : {}
  const pipelineType = resolvePipelineType([
    source.pipeline_type,
    source.pipelineType,
    setting.pipeline_type,
    setting.pipelineType,
    hints.pipeline_type,
    hints.pipelineType
  ])
  const modelFormat = firstNonBlankString([
    source.model_format,
    source.modelFormat,
    setting.model_format,
    setting.modelFormat,
    hints.model_format,
    hints.modelFormat
  ])

  return {
    ...source,
    ...(pipelineType !== undefined ? { pipeline_type: pipelineType } : {}),
    ...(modelFormat !== undefined ? { model_format: modelFormat } : {})
  }
}

const parseBooleanLike = value => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toLowerCase()
  if (['false', '0', 'no', 'off'].includes(normalized)) return false
  if (['true', '1', 'yes', 'on'].includes(normalized)) return true
  return undefined
}

const readBackgroundRemoval = source => {
  if (!isRecord(source)) return undefined
  return source.bg_remove ?? source.bgRemove ?? source.background_remove ?? source.backgroundRemoval
}

/** Match RenderTask's direct + params/detail/options/config bg_remove lookup. */
export function resolveBackgroundRemoval(task) {
  const detail = parseJsonRecord(task)
  if (!detail) return undefined
  const direct = parseBooleanLike(readBackgroundRemoval(detail))
  if (direct !== undefined) return direct

  for (const key of ['params', 'detail', 'options', 'config']) {
    const nested = parseJsonRecord(detail[key]) || detail[key]
    const value = parseBooleanLike(readBackgroundRemoval(nested))
    if (value !== undefined) return value
  }
  return undefined
}

/** Single-image 3DGS tasks always use their captured camera viewing window. */
export function isImageTo3dgsTask(task) {
  const pipeline = String(task?.pipeline_type ?? task?.pipelineType ?? '').trim().toLowerCase()
  return ['image_to_3dgs', 'image_3dgs', 'single_image_3dgs'].includes(pipeline)
}

/** AR placement is limited to generated/reconstructed isolated objects. */
export function supportsArProjection(task) {
  const pipeline = String(task?.pipeline_type ?? task?.pipelineType ?? '').trim().toLowerCase()
  if (pipeline === 'direct_upload') return false
  if (pipeline === 'ai_gen_object') return true
  return resolveBackgroundRemoval(task) === true
}

/**
 * Use the model AABB as the stable orbit focus for AI generated and explicitly
 * background-removed tasks. When the flag is absent, preserve the captured
 * camera-data focus; project/community payloads can omit that legacy flag and
 * must still open at the same position.
 */
export function usesAabbOrbitCenter(task) {
  // A single-image task must retain the focus solved from cameraData rays,
  // even when the task also carries a background-removal flag.
  if (isImageTo3dgsTask(task)) return false
  const pipeline = String(task?.pipeline_type ?? task?.pipelineType ?? '').trim().toLowerCase()
  if (pipeline === 'ai_gen_object') return true
  return resolveBackgroundRemoval(task) === true
}

/** Spatial models should be browsed freely instead of inside an object orbit window. */
export function isSpatialModel(task) {
  // image_to_3dgs may be classified as a space by older task payloads, but its
  // valid view is still the cameraData capture window.
  if (isImageTo3dgsTask(task)) return false
  const modelType = String(task?.model_type ?? task?.modelType ?? '').trim().toLowerCase()
  return ['space', 'indoor', 'outdoor'].includes(modelType)
}

export function readRenderTaskNumber(value, fallback, range) {
  if (value === undefined || value === null || value === '') return fallback
  const number = Number(value)
  if (!Number.isFinite(number)) return fallback
  if (!range) return number
  return Math.min(Math.max(number, range[0]), range[1])
}

export function resolveViewerInteractionSpeeds(options = {}) {
  const spatialModel = Boolean(options.spatialModel)
  const imageTo3dgs = Boolean(options.imageTo3dgs)
  if (!options.embedded) {
    const configuredMoveSpeed = options.moveSpeed
    const configuredPinchSpeed = options.pinchSpeed
    return {
      moveSpeed: readRenderTaskNumber(
        configuredMoveSpeed,
        imageTo3dgs && (configuredMoveSpeed === undefined || configuredMoveSpeed === null || configuredMoveSpeed === '')
          ? IMAGE_TO_3DGS_MOVE_SPEED
          : spatialModel && (configuredMoveSpeed === undefined || configuredMoveSpeed === null || configuredMoveSpeed === '')
            ? WEB_SPATIAL_MOVE_SPEED
            : RENDER_TASK_DEFAULT_SETTINGS.moveSpeed,
        RENDER_TASK_SETTING_RANGES.moveSpeed
      ),
      pinchSpeed: readRenderTaskNumber(
        configuredPinchSpeed,
        spatialModel && (configuredPinchSpeed === undefined || configuredPinchSpeed === null || configuredPinchSpeed === '')
          ? WEB_SPATIAL_PINCH_SPEED
          : RENDER_TASK_DEFAULT_SETTINGS.pinchSpeed,
        RENDER_TASK_SETTING_RANGES.pinchSpeed
      )
    }
  }

  if (imageTo3dgs) {
    return { moveSpeed: IMAGE_TO_3DGS_MOVE_SPEED, pinchSpeed: RENDER_TASK_DEFAULT_SETTINGS.pinchSpeed }
  }

  return spatialModel
    ? { moveSpeed: MINIAPP_SPATIAL_MOVE_SPEED, pinchSpeed: MINIAPP_SPATIAL_PINCH_SPEED }
    : {
        moveSpeed: MINIAPP_OBJECT_MOVE_SPEED,
        pinchSpeed: RENDER_TASK_DEFAULT_SETTINGS.pinchSpeed
      }
}

export function normalizeOrbitMotionType(value) {
  const normalized = String(value ?? '').trim()
  return ['orbit', 'ellipse', 'spiral', 'dolly', 'swing', 'captureTrajectory', 'custom'].includes(normalized)
    ? normalized
    : RENDER_TASK_DEFAULT_SETTINGS.orbitMotionType
}
